import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Bot, Plus, MessageSquare, Clock, Search } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getVagaAssistantSessions } from "@/services/agents-api";
import type { VagaAssistantSessionSummary } from "@/types/agents";

interface SessionHistoryListProps {
  onSelectSession: (sessionId: string, vagaId: string) => void;
  onNewConversation: () => void;
  currentVagaId?: string;
}

export function SessionHistoryList({
  onSelectSession,
  onNewConversation,
  currentVagaId,
}: SessionHistoryListProps) {
  const [filter, setFilter] = useState<"all" | "vaga">("all");
  const [search, setSearch] = useState("");

  const { data: sessions, isLoading } = useQuery({
    queryKey: ["vaga-assistant-sessions"],
    queryFn: () => getVagaAssistantSessions(30),
    staleTime: 30_000,
  });

  const filteredSessions = useMemo(() => {
    let list = sessions ?? [];
    if (filter === "vaga" && currentVagaId) {
      list = list.filter((s) => s.vaga_id === currentVagaId);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((s) =>
        (s.title || "").toLowerCase().includes(q) ||
        (s.vaga_titulo || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [sessions, filter, currentVagaId, search]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b space-y-2 shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Histórico</h3>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={onNewConversation}
          >
            <Plus className="h-3 w-3" />
            Nova conversa
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título ou vaga..."
            className="h-7 pl-7 text-xs"
          />
        </div>

        {currentVagaId && (
          <div className="flex gap-1">
            <button
              onClick={() => setFilter("all")}
              className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                filter === "all"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-transparent text-muted-foreground border-border hover:bg-muted"
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilter("vaga")}
              className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                filter === "vaga"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-transparent text-muted-foreground border-border hover:bg-muted"
              }`}
            >
              Esta vaga
            </button>
          </div>
        )}
      </div>

      {/* Lista */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {isLoading && (
            <div className="space-y-2 p-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-1.5">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          )}

          {!isLoading && filteredSessions.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">
              <Bot className="h-8 w-8 mx-auto mb-2 opacity-50" />
              {search.trim() ? (
                <>
                  <p>Nada encontrado para "{search}".</p>
                  <p className="text-xs mt-1">Tente outro termo.</p>
                </>
              ) : (
                <>
                  <p>Nenhuma conversa anterior.</p>
                  <p className="text-xs mt-1">
                    Inicie uma nova conversa para começar.
                  </p>
                </>
              )}
            </div>
          )}

          {filteredSessions.map((session) => (
            <SessionCard
              key={session.session_id}
              session={session}
              isCurrentVaga={session.vaga_id === currentVagaId}
              onClick={() => onSelectSession(session.session_id, session.vaga_id)}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

function SessionCard({
  session,
  isCurrentVaga,
  onClick,
}: {
  session: VagaAssistantSessionSummary;
  isCurrentVaga: boolean;
  onClick: () => void;
}) {
  const timeAgo = formatDistanceToNow(new Date(session.last_activity_at), {
    addSuffix: true,
    locale: ptBR,
  });

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors hover:bg-muted group ${
        isCurrentVaga ? "border border-purple-200/50" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">
            {session.title || "Conversa sem título"}
          </p>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {session.vaga_titulo}
          </p>
        </div>
        <Badge variant="secondary" className="h-4 px-1 text-[10px] shrink-0">
          <MessageSquare className="h-2.5 w-2.5 mr-0.5" />
          {session.message_count}
        </Badge>
      </div>
      <div className="flex items-center gap-1 mt-1 text-[11px] text-muted-foreground">
        <Clock className="h-2.5 w-2.5" />
        <span>{timeAgo}</span>
      </div>
    </button>
  );
}
